<template>
  <div class="md-layout">
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">tune</span>
          </div>
          <h4 class="title">{{ $t("layout.oqsSettingsMenu") }}</h4>
          <md-button v-if="canEdit" class="md-success oqs-save" :disabled="saving || !dirty || !valid" @click="save">{{ $t("oqsSettings.save") }}</md-button>
        </md-card-header>
        <md-card-content>
          <p class="oqs-intro">{{ $t("oqsSettings.intro") }}</p>
          <p class="oqs-formula">Score = 100 × max(0; min(1; (Wert − Niedrigwert) / (Höchstwert − Niedrigwert)))</p>
          <p v-if="!canEdit" class="oqs-intro">{{ $t("oqsSettings.readOnly") }}</p>

          <div class="oqs-sum" :class="sumOk ? 'oqs-sum--ok' : 'oqs-sum--bad'">
            <span>{{ $t("oqsSettings.sum") }}</span>
            <strong>{{ fmt(sum, 1) }} %</strong>
            <span v-if="!sumOk" class="oqs-sum-hint">{{ sum > 100 ? $t("oqsSettings.sumTooHigh", { diff: fmt(sum - 100, 1) }) : $t("oqsSettings.sumTooLow", { diff: fmt(100 - sum, 1) }) }}</span>
          </div>

          <div v-for="area in areas" :key="area" class="oqs-area">
            <h5 class="oqs-area-title">
              {{ $t("oqsSettings.areas." + area) }}
              <span class="oqs-area-sum">{{ fmt(areaSum(area), 1) }} %</span>
            </h5>
            <table class="oqs-table">
              <thead>
                <tr>
                  <th>{{ $t("oqsSettings.score") }}</th>
                  <th class="oqs-weight-col">{{ $t("oqsSettings.weight") }}</th>
                  <th class="oqs-limit-col">{{ $t("oqsSettings.min") }}</th>
                  <th class="oqs-limit-col">{{ $t("oqsSettings.max") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rowsOf(area)" :key="row.key">
                  <td class="oqs-name">{{ $t("oqsSettings.keys." + row.key) }}</td>
                  <td class="oqs-weight-col">
                    <span class="oqs-weight">
                      <input v-model.number="row.weight" type="range" min="0" max="50" step="0.5" class="oqs-slider" :disabled="!canEdit" :style="sliderStyle(row.weight)" />
                      <span class="oqs-cell">
                        <input v-model.number="row.weight" type="number" min="0" max="100" step="0.5" :disabled="!canEdit" :class="{ 'oqs-invalid': !weightValid(row) }" />
                        <span class="oqs-unit">%</span>
                      </span>
                    </span>
                  </td>
                  <td class="oqs-limit-col">
                    <span class="oqs-cell">
                      <input v-model.number="row.min" type="number" step="any" :disabled="!canEdit" :class="{ 'oqs-invalid': !limitsValid(row) }" />
                      <span class="oqs-unit">{{ $t("oqsSettings.units." + unitOf(row.key)) }}</span>
                    </span>
                  </td>
                  <td class="oqs-limit-col">
                    <span class="oqs-cell">
                      <input v-model.number="row.max" type="number" step="any" :disabled="!canEdit" :class="{ 'oqs-invalid': !limitsValid(row) }" />
                      <span class="oqs-unit">{{ $t("oqsSettings.units." + unitOf(row.key)) }}</span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="oqs-hint">{{ $t("oqsSettings.hint") }}</p>
        </md-card-content>
      </md-card>
    </div>
  </div>
</template>

<script>
import { hasPermission } from "../../mixins/auth";
import oqsSettingsService from "../../services/oqs-settings.service";

const AREAS = ["sales", "range", "margin"];

export default {
  data() {
    return { rows: [], original: "", saving: false, areas: AREAS };
  },
  computed: {
    canEdit() {
      return hasPermission("Write", "OqsSettings", "Edit");
    },
    snapshot() {
      return JSON.stringify(this.rows.map((r) => [r.key, r.weight, r.min, r.max]));
    },
    dirty() {
      return this.snapshot !== this.original;
    },
    sum() {
      return Math.round(this.rows.reduce((s, r) => s + (Number(r.weight) || 0), 0) * 100) / 100;
    },
    sumOk() {
      return this.sum === 100;
    },
    valid() {
      return this.sumOk && this.rows.every((r) => this.weightValid(r) && this.limitsValid(r));
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      return oqsSettingsService
        .getSettings()
        .then((items) => this.setRows(items))
        .catch(() => this.notify("danger", this.$t("oqsSettings.loadFailed")));
    },
    setRows(items) {
      this.rows = items.map((i) => ({ key: i.key, area: i.area, weight: i.weight, min: i.min, max: i.max }));
      this.$nextTick(() => {
        this.original = this.snapshot;
      });
    },
    rowsOf(area) {
      return this.rows.filter((r) => r.area === area);
    },
    areaSum(area) {
      return this.rowsOf(area).reduce((s, r) => s + (Number(r.weight) || 0), 0);
    },
    unitOf(key) {
      if (key.startsWith("Sales")) return "pieces";
      if (key.startsWith("Range")) return "months";
      return key.endsWith("Percent") ? "percent" : "eur";
    },
    weightValid(row) {
      const v = row.weight;
      return v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) >= 0 && Number(v) <= 100;
    },
    limitsValid(row) {
      const isNum = (v) => v !== "" && v != null && Number.isFinite(Number(v));
      return isNum(row.min) && isNum(row.max) && Number(row.min) !== Number(row.max);
    },
    // gefuellter Teil des Reglers in Gruen, Rest grau
    sliderStyle(weight) {
      const pct = Math.min(100, ((Number(weight) || 0) / 50) * 100);
      return { background: `linear-gradient(to right, #7f915a ${pct}%, #ddd ${pct}%)` };
    },
    fmt(value, decimals = 2) {
      return this.$options.filters.formatNumberGerman(value, decimals);
    },
    save() {
      this.saving = true;
      const updates = this.rows.map((r) => ({ key: r.key, weight: Number(r.weight), min: Number(r.min), max: Number(r.max) }));
      return oqsSettingsService
        .saveSettings(updates)
        .then((items) => {
          this.setRows(items);
          this.notify("success", this.$t("oqsSettings.saved"));
        })
        .catch((e) => {
          if (e && e.response && e.response.status === 403) return;
          const reason = e && e.response && e.response.data && e.response.data.reason;
          this.notify("danger", reason && this.$te("oqsSettings.errors." + reason) ? this.$t("oqsSettings.errors." + reason) : this.$t("oqsSettings.saveFailed"));
        })
        .finally(() => {
          this.saving = false;
        });
    },
    notify(type, message) {
      this.$notify({ timeout: 2500, message, icon: "add_alert", horizontalAlign: "right", verticalAlign: "top", type });
    },
  },
};
</script>

<style scoped>
.md-card .md-card-header.md-card-header-icon .material-symbols-outlined {
  width: 33px;
  height: 33px;
  text-align: center;
  line-height: 33px;
  color: #fff;
  margin: 0 !important;
  font-size: 28px !important;
}
.oqs-save {
  float: right;
  margin-top: -6px;
}
.oqs-intro,
.oqs-hint {
  color: #777;
  font-size: 13px;
  margin: 0 0 8px;
}
.oqs-hint {
  margin: 16px 0 0;
  font-size: 12px;
}
.oqs-formula {
  font-family: "Roboto Mono", monospace;
  font-size: 12px;
  color: #555;
  background: #f6f6f6;
  display: inline-block;
  padding: 4px 8px;
  border-radius: 3px;
  margin: 0 0 12px;
}
.oqs-sum {
  display: flex;
  align-items: baseline;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 8px;
}
.oqs-sum strong {
  font-size: 18px;
}
.oqs-sum--ok {
  background: #f1f5e8;
  color: #7f915a;
}
.oqs-sum--bad {
  background: #ffebee;
  color: #c62828;
}
.oqs-sum-hint {
  font-size: 13px;
}
.oqs-area {
  margin-top: 16px;
}
.oqs-area-title {
  margin: 0 0 4px;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  max-width: 900px;
}
.oqs-area-sum {
  color: #777;
  font-weight: 400;
}
.oqs-table {
  width: 100%;
  max-width: 900px;
  border-collapse: collapse;
  /* feste Spalten, damit die drei Bereiche untereinander buendig stehen */
  table-layout: fixed;
}
.oqs-table th:first-child {
  width: 230px;
}
.oqs-weight-col {
  width: 330px;
}
.oqs-limit-col {
  width: 170px;
}
.oqs-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 500;
  color: #7f915a;
  padding: 6px 8px;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}
.oqs-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}
.oqs-name {
  font-weight: 500;
  min-width: 170px;
}
.oqs-weight {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}
.oqs-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 180px;
  height: 6px;
  border-radius: 3px;
  outline: none;
}
.oqs-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #7f915a;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}
.oqs-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #7f915a;
  border: 2px solid #fff;
  cursor: pointer;
}
.oqs-slider:disabled::-webkit-slider-thumb {
  background: #aaa;
  cursor: default;
}
.oqs-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.oqs-cell input {
  width: 80px;
  padding: 4px 6px;
  border: 1px solid #ddd;
  border-radius: 3px;
  text-align: right;
}
.oqs-cell input:disabled {
  background: #f6f6f6;
  color: #555;
}
.oqs-invalid {
  border-color: #f44336 !important;
}
.oqs-unit {
  color: #777;
  font-size: 12px;
  min-width: 44px;
}
</style>
